import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton, SkeletonLine, SkeletonRegion } from "@/components/ui/skeleton";
import { moment } from "@/i18n/datum";
import {
  BRON,
  STATUS,
  type Bron,
  type Synchronisatie,
  useStartSynchronisatie,
  useSynchronisaties,
} from "./useSynchronisaties";

/** One card per bron: what the last run did, and the button that runs it now (ADR-0067). */
export function Synchronisaties() {
  const { t } = useTranslation("synchronisaties");
  const { data, isPending, error } = useSynchronisaties();

  if (isPending) {
    return <SynchronisatiesSkeleton />;
  }

  if (error) {
    return <p className="text-sm text-destructive">{error.message}</p>;
  }

  return (
    <div className="space-y-4">
      {/* Said once above both cards rather than on each: no jobs service is a fact about the
          environment, and it is why a card can sit on "bezig" with nothing moving. */}
      {data && !data.jobsServiceRunning && (
        <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {t("geenAchtergrondservice")}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {(data?.bronnen ?? []).map((sync) => (
          <SynchronisatieCard key={sync.bron} sync={sync} />
        ))}
      </div>
    </div>
  );
}

// Two cards, not a row count from data: the catalog always has exactly vbs and portaal.
function SynchronisatiesSkeleton() {
  return (
    <SkeletonRegion className="grid gap-4 md:grid-cols-2">
      {[0, 1].map((index) => (
        <Card key={index}>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2">
                <SkeletonLine size="base" className="w-32" />
                <SkeletonLine size="sm" className="w-48" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <SkeletonLine size="sm" className="w-40" />
              <SkeletonLine size="sm" className="w-40" />
            </div>
            <Skeleton className="h-9 w-40" />
          </CardContent>
        </Card>
      ))}
    </SkeletonRegion>
  );
}

function SynchronisatieCard({ sync }: { sync: Synchronisatie }) {
  const { t } = useTranslation("synchronisaties");
  const start = useStartSynchronisatie();

  const running = sync.status === STATUS.running;
  const words = bronWoorden(sync.bron as Bron, t);

  return (
    <Card>
      <SynchronisatieCardKop words={words} status={sync.status} />

      <CardContent className="space-y-4">
        <SynchronisatieTijden sync={sync} t={t} />

        {sync.errorMessage && (
          <SynchronisatieFoutmelding running={running} bericht={sync.errorMessage} t={t} />
        )}

        <SynchronisatieStartActie sync={sync} running={running} start={start} t={t} />
      </CardContent>
    </Card>
  );
}

// Branched rather than keyed on the bron: the catalog is the type, so a computed
// `bronnen.${bron}.naam` does not typecheck.
function bronWoorden(bron: Bron, t: TFunction<"synchronisaties">) {
  return bron === BRON.vbs
    ? { naam: t("bronnen.vbs.naam"), wat: t("bronnen.vbs.wat") }
    : { naam: t("bronnen.portaal.naam"), wat: t("bronnen.portaal.wat") };
}

function SynchronisatieCardKop({
  words,
  status,
}: {
  words: { naam: string; wat: string };
  status: Synchronisatie["status"];
}) {
  return (
    <CardHeader>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle>{words.naam}</CardTitle>
          <CardDescription>{words.wat}</CardDescription>
        </div>
        <StatusBadge status={status} />
      </div>
    </CardHeader>
  );
}

function SynchronisatieTijden({
  sync,
  t,
}: {
  sync: Synchronisatie;
  t: TFunction<"synchronisaties">;
}) {
  return (
    <dl className="space-y-1 text-sm">
      <div className="flex gap-2">
        <dt className="text-muted-foreground">{t("laatsteKeer")}</dt>
        <dd>{sync.lastRunOn ? moment(sync.lastRunOn) : t("nogNietGelopen")}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="text-muted-foreground">{t("volgendeKeer")}</dt>
        {/* No next occurrence is how a switched-off schedule reaches this screen. */}
        <dd>{sync.nextRunOn ? moment(sync.nextRunOn) : t("nietIngepland")}</dd>
      </div>
    </dl>
  );
}

function SynchronisatieFoutmelding({
  running,
  bericht,
  t,
}: {
  running: boolean;
  bericht: string;
  t: TFunction<"synchronisaties">;
}) {
  return (
    <div className="space-y-1 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
      {/* Shown while the job is still retrying too, so say which of the two this is. */}
      <p className="font-bold">{running ? t("pogingMislukt") : t("synchronisatieMislukt")}</p>
      <p>{bericht}</p>
    </div>
  );
}

function SynchronisatieStartActie({
  sync,
  running,
  start,
  t,
}: {
  sync: Synchronisatie;
  running: boolean;
  start: ReturnType<typeof useStartSynchronisatie>;
  t: TFunction<"synchronisaties">;
}) {
  return (
    <div className="space-y-2">
      <Button
        onClick={() => start.mutate(sync.bron as Bron)}
        disabled={running || start.isPending}
      >
        {running || start.isPending ? (
          // sensor:allow codox-spinner pending button, not a loading state
          <Loader2 className="animate-spin" />
        ) : (
          <RefreshCw />
        )}
        {running ? t("bezig") : t("nuSynchroniseren")}
      </Button>

      {start.error && <p className="text-sm text-destructive">{start.error.message}</p>}
    </div>
  );
}

/** Rendered as a badge but written as the word, so a screen reader says which state this is. */
function StatusBadge({ status }: { status: string | null }) {
  const { t } = useTranslation("synchronisaties");

  if (status === STATUS.failed) {
    return <Badge variant="destructive">{t("status.mislukt")}</Badge>;
  }

  if (status === STATUS.running) {
    return <Badge variant="muted">{t("status.bezig")}</Badge>;
  }

  if (status === STATUS.succeeded) {
    return <Badge>{t("status.geslaagd")}</Badge>;
  }

  // Null: this bron has never run here.
  return <Badge variant="outline">{t("status.nooit")}</Badge>;
}
